import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../System Design/Button";
import FeatureCard from "../../System Design/FeatureCard";
import DocumentItem from "../../System Design/DocumentItem";
import { Search, FileText, BookOpen } from "lucide-react";
import bookService from "../../services/bookService";
import authService from "../../services/authService";
import { useLanguage } from "../../contexts/LanguageContext";

const Home = () => {
  const navigate = useNavigate();
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isAdmin = authService.isAdmin();

  const { t, language } = useLanguage();

  const getLocaleForLanguage = (lang) => {
    if (lang === 'en') return 'en-US';
    if (lang === 'pt') return 'pt-PT';
    return 'fr-FR';
  };

  const dateLocale = getLocaleForLanguage(language);

  // Charger les documents récents
  useEffect(() => {
    const fetchRecentDocuments = async () => {
      try {
        setIsLoading(true);
        const response = await bookService.getRecentBooks();
        setRecentDocuments(response.results || []);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des documents récents:', err);
        setError(t('home.errors.loadRecent'));
        setRecentDocuments([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentDocuments();
  }, [t]);

  // Gestionnaires d'événements
  const handleUploadNew = () => {
    navigate('/upload');
  };

  const handleViewLibrary = () => {
    navigate('/documents');
  };

  const handleViewDocument = (docId) => {
    console.log(docId);
    navigate(`/documents/${docId}`);
  };

  return (
    <div className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto">
      {/* Hero */}
      <div className="bg-primary-50 rounded-xl p-6 sm:p-8 md:p-10 text-center">
        <h1 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">
          {t('home.heroTitlePart1')} <span className="text-primary">{t('home.heroTitleHighlight')}</span>
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
          {t('home.heroSubtitle')}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
          {isAdmin && (
            <Button variant="primary" onClick={handleUploadNew}>{t('home.heroUploadButton')}</Button>
          )}
          <Button variant="accent" onClick={handleViewLibrary}>{t('home.heroLibraryButton')}</Button>
        </div>
      </div>

      {/* Features */}
      <h2 className="text-lg sm:text-xl font-bold mt-8 sm:mt-12 mb-4 sm:mb-6 text-center">
        {t('home.featuresTitle')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
        <FeatureCard icon={<Search size={24} />} title={t('home.feature1Title')} description={t('home.feature1Description')} />
        <FeatureCard icon={<BookOpen size={24} />} title={t('home.feature2Title')} description={t('home.feature2Description')} />
        <FeatureCard icon={<FileText size={24} />} title={t('home.feature3Title')} description={t('home.feature3Description')} />
      </div>
      
      {/* Statistiques */}
      <div className="mt-10 sm:mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-success-50 border border-success-200 rounded-xl p-6 text-center">
          <div className="text-3xl font-bold text-success mb-2">100%</div>
          <div className="text-sm text-success-700">{t('home.statsAnalysis')}</div>
        </div>
        <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 text-center">
          <div className="text-3xl font-bold text-accent mb-2">24/7</div>
          <div className="text-sm text-accent-700">{t('home.statsAvailability')}</div>
        </div>
        <div className="bg-warning-50 border border-warning-200 rounded-xl p-6 text-center">
          <div className="text-3xl font-bold text-warning mb-2">∞</div>
          <div className="text-sm text-warning-700">{t('home.statsStorage')}</div>
        </div>
      </div>

      {/* Documents récents */}
      <div className="mt-8 sm:mt-10 md:mt-12">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl font-bold">{t('home.recentDocumentsTitle')}</h2>
          <button 
            className="text-primary hover:text-primary text-sm"
            onClick={handleViewLibrary}
          >
            {t('home.recentDocumentsSeeAll')}
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-gray-500">
            <p>{error}</p>
          </div>
        ) : recentDocuments.length > 0 ? (
          <div className="flex flex-col gap-3 sm:gap-4">
            {recentDocuments.map((doc) => (
              <DocumentItem 
                key={doc.id}
                title={doc.title}
                date={new Date(doc.created_at).toLocaleDateString(dateLocale)}
                sections={doc.sections_count || 0}
                onClick={() => handleViewDocument(doc.id)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>{t('home.recentDocumentsEmpty')}</p>
            {isAdmin ? (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={handleUploadNew}
              >
                {t('home.recentDocumentsFirstUpload')}
              </Button>
            ) : (
              <Button 
                variant="accent" 
                className="mt-4"
                onClick={handleViewLibrary}
              >
                {t('home.heroLibraryButton')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
